export const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

export const formatDateOnly = (dateInput) => {
    if (!dateInput) return '-';
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return dateInput;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (e) {
        return dateInput;
    }
};

export const formatAmount = (price) => {
  if (price == null || isNaN(price)) return 0;
  price = Number(price);

  if (Number.isInteger(price) || price.toFixed(2).endsWith('.00')) {
    return parseInt(price);
  }
  return Number(price.toFixed(2));
};

export const getMapboxToken = () => {
    const day = new Date().getDate();
    if (day <= 6) {
        return process.env.REACT_APP_MAPBOX_TOKEN || '';
    } else if (day <= 12) {
        return process.env.REACT_APP_MAPBOX_TOKEN_2 || '';
    } else if (day <= 18) {
        return process.env.REACT_APP_MAPBOX_TOKEN_3 || '';
    } else if (day <= 24) {
        return process.env.REACT_APP_MAPBOX_TOKEN_4 || '';
    } else {
        return process.env.REACT_APP_MAPBOX_TOKEN_5 || '';
    }
};

export const normalizeToYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  
  if (dateStr instanceof Date) {
    return formatDateOnly(dateStr);
  }
  
  const match = String(dateStr).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }
  return dateStr;
};

export const getWebDetails = () => {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "Unknown";
    
    if (userAgent.indexOf("Firefox") > -1) {
        browserName = "Mozilla Firefox";
        const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    } else if (userAgent.indexOf("SamsungBrowser") > -1) {
        browserName = "Samsung Internet";
        const match = userAgent.match(/SamsungBrowser\/(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
        browserName = "Opera";
        const match = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    } else if (userAgent.indexOf("Trident") > -1) {
        browserName = "Microsoft Internet Explorer";
        const match = userAgent.match(/rv:(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    } else if (userAgent.indexOf("Edge") > -1 || userAgent.indexOf("Edg") > -1) {
        browserName = "Microsoft Edge";
        const match = userAgent.match(/(?:Edge|Edg)\/(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    } else if (userAgent.indexOf("Chrome") > -1) {
        browserName = "Google Chrome";
        const match = userAgent.match(/Chrome\/(\d+\.\d+\.\d+)/);
        if (match) browserVersion = match[1] + ".";
    } else if (userAgent.indexOf("Safari") > -1) {
        browserName = "Apple Safari";
        const match = userAgent.match(/Version\/(\d+\.\d+)/);
        if (match) browserVersion = match[1];
    }

    return {
        webVersion: browserVersion,
        webBrowser: browserName,
        webInfo: userAgent
    };
};

const directionsCache = new Map();

export const getBatchDirections = async (seq, accessToken, profile = 'driving') => {
    if (!accessToken || accessToken === '') {
        return seq;
    }

    // 1. Sanitize input: ensure we only have valid coordinate arrays
    const cleanSeq = (seq || []).filter(c => 
        Array.isArray(c) && 
        c.length === 2 && 
        c[0] !== null && 
        c[1] !== null && 
        !isNaN(c[0]) && 
        !isNaN(c[1])
    );

    if (cleanSeq.length <= 1) return cleanSeq;

    // 2. Prevent duplicate adjacent coordinates (Mapbox Directions API throws 422 for consecutive duplicate coords)
    const dedupedSeq = [];
    for (let i = 0; i < cleanSeq.length; i++) {
        if (i === 0) {
            dedupedSeq.push(cleanSeq[i]);
        } else {
            const prev = cleanSeq[i - 1];
            const curr = cleanSeq[i];
            // Check if coordinates are functionally identical (within ~11 millimeters precision)
            if (Math.abs(prev[0] - curr[0]) > 1e-7 || Math.abs(prev[1] - curr[1]) > 1e-7) {
                dedupedSeq.push(curr);
            }
        }
    }

    if (dedupedSeq.length <= 1) return dedupedSeq;

    // 3. Cache lookup
    const cacheKey = `${profile}|` + dedupedSeq.map(c => `${c[0].toFixed(6)},${c[1].toFixed(6)}`).join(';');
    if (directionsCache.has(cacheKey)) {
        return directionsCache.get(cacheKey);
    }

    const maxChunkSize = 25;
    const promises = [];
    const chunks = [];

    // 4. Divide into chunks and build promises for parallel fetching
    for (let i = 0; i < dedupedSeq.length - 1; i += (maxChunkSize - 1)) {
        const chunk = dedupedSeq.slice(i, i + maxChunkSize);
        if (chunk.length <= 1) break;
        chunks.push(chunk);

        const coordsStr = chunk.map(c => `${c[0]},${c[1]}`).join(';');
        const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordsStr}?alternatives=false&geometries=geojson&overview=full&access_token=${accessToken}`;
        
        promises.push(
            fetch(url)
                .then(r => {
                    if (!r.ok) {
                        throw new Error(`Mapbox API error: ${r.statusText}`);
                    }
                    return r.json();
                })
                .then(j => j?.routes?.[0]?.geometry?.coordinates || null)
                .catch(e => {
                    console.error('Mapbox Directions chunk failed:', e.message);
                    return null;
                })
        );
    }

    // 5. Fetch all chunks in parallel
    const results = await Promise.all(promises);

    // 6. Combine chunk results
    const allLegCoords = [];
    for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        const routeCoords = results[i];

        if (routeCoords && routeCoords.length > 0) {
            if (allLegCoords.length > 0) {
                // Avoid duplicating the overlapping point at chunk boundaries
                allLegCoords.push(...routeCoords.slice(1));
            } else {
                allLegCoords.push(...routeCoords);
            }
        } else {
            // Fallback to straight lines for failed chunk
            if (allLegCoords.length > 0) {
                allLegCoords.push(...chunk.slice(1));
            } else {
                allLegCoords.push(...chunk);
            }
        }
    }

    // 7. Store in cache
    directionsCache.set(cacheKey, allLegCoords);

    return allLegCoords;
};

